import styled, { css } from "styled-components";
import { calcRem } from "styles/theme";
import { FLEX } from "styles/flex";
import * as mediaQuery from "components/MediaQuery";
import { COLORS } from "styles/colors";

export const Page = styled.div`
  min-height: calc(100vh - 180px);
  ${FLEX.FlexCenterStart};
  margin: 50px auto;

  ${mediaQuery.isMobile} {
    align-items: flex-start;
  }
`;

export const Card = styled.div`
  width: 100%;
  max-width: 760px;
  background: ${({ theme }) => theme.COLORS.FILTERITEM};
  border-radius: 6px;
  box-shadow: 0 18px 40px ${COLORS.BLACK}30;
`;

export const Inner = styled.div`
  padding: 20px 26px;
  min-height: 640px;
  box-sizing: border-box;
  width: 100%;

  ${mediaQuery.isMobile} {
    min-height: auto;
  }

  .stepper-container {
    width: 100%;
  }

  .inner-content {
    padding: 0 80px;
    width: 100%;
  }
`;

export const Title = styled.h1`
  text-align: center;
  color: ${({ theme }) => theme.COLORS.WHITE};
  font-size: ${calcRem(24)};
  font-weight: 500;
  padding: 10px 0 30px;
`;

export const CenterStage = styled.div`
  ${FLEX.FlexCenterCenter};
  flex-direction: column;
  text-align: center;
  padding: 20px 0;

  .icon {
    ${FLEX.FlexCenterCenter};
    margin-bottom: 20px;
    color: ${({ theme }) => theme.COLORS.GREEN};

    &.storage svg {
      width: 26px;
      height: 26px;
    }

    &.review {
      width: 60px;
      height: 60px;
      border-radius: 16px;
      background: ${({ theme }) => `${theme.COLORS.BUTTONS2}18`};
      color: ${({ theme }) => theme.COLORS.BUTTONS2};

      svg {
        width: 28px;
        height: 28px;
      }
    }

    &.channel {
      width: 60px;
      height: 60px;
      border-radius: 16px;
      background: ${({ theme }) => `${theme.COLORS.BUTTONS2}18`};
      color: ${({ theme }) => theme.COLORS.BUTTONS2};

      svg {
        width: 28px;
        height: 28px;
      }
    }

    &.storage {
      width: 60px;
      height: 60px;
      border-radius: 16px;
      background: ${({ theme }) => `${theme.COLORS.BUTTONS2}18`};
      color: ${({ theme }) => theme.COLORS.BUTTONS2};
    }
  }

  h2 {
    color: ${({ theme }) => theme.COLORS.WHITE};
    font-size: ${calcRem(18)};
    font-weight: 500;
    margin-bottom: 10px;
  }

  p {
    max-width: 380px;
    color: ${({ theme }) => theme.COLORS.BUTTONS3};
    font-size: ${calcRem(14)};
    line-height: 1.45;
  }
`;

export const ChannelInfoCard = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 1px minmax(0, 1fr);
  gap: 22px;
  border: 1px solid ${({ theme }) => theme.COLORS.BUTTONS3};
  border-radius: 10px;
  padding: 18px 18px 16px;
  margin-bottom: 22px;
  background: ${({ theme }) => `${theme.COLORS.WIDGETCOLOR}40`};

  .column {
    min-width: 0;
  }

  .divider {
    width: 1px;
    background: ${({ theme }) => `${theme.COLORS.WHITE}10`};
  }

  h3 {
    ${FLEX.FlexStartCenter};
    gap: 8px;
    margin-bottom: 14px;
    color: ${({ theme }) => theme.COLORS.WHITE};
    font-size: ${calcRem(16)};
    font-weight: 500;
  }

  ul {
    ${FLEX.FlexStartStart};
    flex-direction: column;
    gap: 10px;
  }

  li {
    ${FLEX.FlexStartStart};
    gap: 8px;
    color: ${({ theme }) => theme.COLORS.WHITE};
    font-size: ${calcRem(14)};
    line-height: 1.45;
  }

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
    color: ${({ theme }) => theme.COLORS.BUTTONS2};
    margin-top: 2px;
  }

  ul.muted li {
    position: relative;
    padding-left: 12px;
    color: ${({ theme }) => theme.COLORS.BUTTONS3};

    &::before {
      content: "";
      position: absolute;
      top: 9px;
      left: 0;
      width: 5px;
      height: 5px;
      border-radius: 999px;
      background: ${({ theme }) => `${theme.COLORS.BUTTONS3}90`};
    }
  }

  ${mediaQuery.isMobile} {
    grid-template-columns: 1fr;
    gap: 18px;

    .divider {
      display: none;
    }
  }
`;

export const SectionLabel = styled.div`
  color: ${({ theme }) => theme.COLORS.WHITE};
  font-size: ${calcRem(14)};
  font-weight: 500;
  margin-bottom: 10px;
`;

export const CycleGrid = styled.ul`
  display: grid;
  grid-auto-flow: column;
  grid-auto-columns: minmax(0, 1fr);
  align-items: stretch;
  gap: 14px;
  margin-bottom: 10px;

  &.single {
    grid-template-columns: 1fr;
    margin-bottom: 22px;
  }

  ${mediaQuery.isMobile} {
    grid-template-columns: 1fr;
  }
`;

export const CycleCard = styled.li<{ $active: boolean; $disabled?: boolean }>`
  ${FLEX.FlexStartStart};
  flex-direction: column;
  gap: 8px;
  align-self: stretch;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.COLORS.BUTTONS3};
  color: ${({ theme }) => theme.COLORS.WHITE};
  padding: 16px;
  box-sizing: border-box;
  background-color: ${({ theme }) => theme.COLORS.WIDGETCOLOR};
  cursor: pointer;
  height: 100%;

  ${({ $disabled }) =>
    $disabled &&
    css`
      opacity: 0.45;
      cursor: not-allowed;
    `}

  ${({ $active, theme }) =>
    $active &&
    css`
      border-color: ${theme.COLORS.SELECTED};
      box-shadow: 0 0 0 1px ${theme.COLORS.SELECTED};
    `}

  h3 {
    font-size: ${calcRem(16)};
    font-weight: 500;
  }

  p {
    color: ${({ theme }) => theme.COLORS.BUTTONS3};
    font-size: ${calcRem(14)};
    line-height: 1.4;
  }

  .header-row {
    ${FLEX.FlexStartCenter};
    align-items: flex-start;
    gap: 8px;
    width: 100%;
  }

  .title-row {
    ${FLEX.FlexStartCenter};
    gap: 6px;
    flex-wrap: wrap;
  }

  strong {
    display: block;
    margin-top: 3px;
    color: ${({ theme }) => theme.COLORS.BUTTONS3};
    font-size: ${calcRem(14)};
  }

  .discount {
    color: ${COLORS.WHITE};
    font-size: ${calcRem(12)};
    font-weight: 500;
    white-space: nowrap;
    background-color: ${({ theme }) => theme.COLORS.BUTTONS2}80;
    border: 1px solid ${({ theme }) => theme.COLORS.BUTTONS2};
    padding: 4px 6px;
    border-radius: 8px;
  }

  .embedded-field-row {
    ${FLEX.FlexBetweenStart};
    flex-direction: column;
    gap: 6px;
    width: 100%;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(115, 129, 164, 0.3);
    min-height: 56px;
  }

  .field-copy {
    min-width: 0;
    flex: 1;
  }

  .field-copy label {
    display: block;
    margin-bottom: 4px;
    color: ${({ theme }) => theme.COLORS.WHITE};
    font-size: ${calcRem(14)};
    font-weight: 500;
    line-height: 1.25;
    white-space: normal;
  }

  .field-copy p {
    color: ${({ theme }) => theme.COLORS.BUTTONS3};
    font-size: ${calcRem(13)};
    line-height: 1.4;
  }

  .field-control {
    ${FLEX.FlexEndEnd};
    gap: 10px;
    flex-shrink: 0;
    color: ${({ theme }) => theme.COLORS.BUTTONS3};
  }

  .field-control span {
    font-size: ${calcRem(14)};
    font-weight: 500;
    white-space: nowrap;
  }

  .field-error {
    width: 100%;
    color: ${COLORS.VALIDATERED};
    font-size: ${calcRem(12)};
    line-height: 1.4;
  }

  .term-row {
    ${FLEX.FlexStartStart};
    flex-direction: column;
    gap: 8px;
    width: 100%;
    margin-top: 14px;
    padding-top: 14px;
    border-top: 1px solid rgba(115, 129, 164, 0.3);
  }

  .term-row label {
    color: ${({ theme }) => theme.COLORS.WHITE};
    font-size: ${calcRem(14)};
    font-weight: 500;
  }

  .segment-row {
    ${FLEX.FlexStartCenter};
    flex-wrap: wrap;
    gap: 8px;
    width: 100%;
  }

  .segment-pill {
    ${FLEX.FlexCenterCenter};
    flex: 1;
    min-width: 64px;
    padding: 6px 10px;
    border-radius: 999px;
    border: 1px solid ${({ theme }) => theme.COLORS.BUTTONS3};
    color: ${({ theme }) => theme.COLORS.BUTTONS3};
    background: transparent;
    font-size: ${calcRem(13)};
    font-weight: 500;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease,
      color 0.15s ease;

    &:hover:not(:disabled) {
      border-color: ${({ theme }) => theme.COLORS.SELECTED};
      color: ${({ theme }) => theme.COLORS.WHITE};
    }

    &.active {
      border-color: ${({ theme }) => theme.COLORS.SELECTED};
      background: ${({ theme }) => theme.COLORS.SELECTED}E6;
      color: ${COLORS.WHITE};

      .qa-tag {
        border: 1px solid ${COLORS.WHITE};
      }
    }

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }
  }

  .segment-pill--qa {
    border-style: dashed;
  }

  .segment-pill .qa-tag {
    color: ${COLORS.WHITE};
    background: ${({ theme }) => theme.COLORS.BUTTONS2}80;
    border: 1px solid ${({ theme }) => theme.COLORS.BUTTONS2};
    border-radius: 4px;
    padding: 1px 5px;
    margin-left: 4px;
    font-size: ${calcRem(10)};
    font-weight: 600;
    letter-spacing: 0.3px;
  }

  ${mediaQuery.isMobile} {
    .embedded-field-row {
      align-items: flex-start;
      flex-direction: column;
      min-height: 0;
    }

    .field-control {
      width: 100%;
      justify-content: flex-start;
    }
  }
`;

export const ToggleCard = styled.div`
  ${FLEX.FlexBetweenCenter};
  gap: 16px;
  border: 1px solid ${({ theme }) => theme.COLORS.BUTTONS3};
  border-radius: 10px;
  padding: 16px 18px;
  margin-bottom: 22px;
  background: ${({ theme }) => `${theme.COLORS.WIDGETCOLOR}40`};

  .copy h3 {
    color: ${({ theme }) => theme.COLORS.WHITE};
    font-size: ${calcRem(16)};
    font-weight: 500;
    margin-bottom: 4px;
  }

  .copy p {
    color: ${({ theme }) => theme.COLORS.BUTTONS3};
    font-size: ${calcRem(14)};
    line-height: 1.4;
  }
`;

export const PriceCard = styled.div`
  ${FLEX.FlexBetweenCenter};
  gap: 16px;
  border: 1px solid ${({ theme }) => theme.COLORS.BUTTONS3};
  border-radius: 6px;
  padding: 14px 16px;
  margin-bottom: 22px;
  width: 100%;

  .copy h3 {
    color: ${({ theme }) => theme.COLORS.WHITE};
    font-weight: 500;
    margin-bottom: 4px;
  }

  .copy p {
    color: ${({ theme }) => theme.COLORS.BUTTONS3};
    font-size: ${calcRem(14)};
    line-height: 1.4;
  }

  .price {
    ${FLEX.FlexEndEnd};
    gap: 2px;
    white-space: nowrap;
    color: ${({ theme }) => theme.COLORS.BUTTONS3};
  }

  strong {
    color: ${({ theme }) => theme.COLORS.WHITE};
    font-size: ${calcRem(28)};
    font-weight: 500;
    line-height: 1;
  }

  span {
    font-size: ${calcRem(14)};
  }

  ${mediaQuery.isMobile} {
    align-items: flex-start;
    flex-direction: column;
  }
`;

export const ChannelCountRow = styled.div`
  ${FLEX.FlexStartCenter};
  gap: 12px;
  margin-bottom: 22px;
  color: ${({ theme }) => theme.COLORS.BUTTONS3};

  .ant-input-number {
    width: 86px;
  }

  span {
    font-size: ${calcRem(14)};
  }
`;

export const ButtonRow = styled.div`
  ${FLEX.FlexCenterCenter};
  gap: 8px;

  button {
    min-width: 88px;
    height: 30px;
    padding: 0 18px;
  }
`;

export const ReviewCard = styled.div`
  border: 1px solid ${({ theme }) => theme.COLORS.BUTTONS3};
  border-radius: 10px;
  padding: 18px 18px 14px;
  margin: 0 auto 18px;
  max-width: 560px;
  background: ${({ theme }) => `${theme.COLORS.WIDGETCOLOR}28`};

  .line-items {
    display: grid;
    gap: 0;
  }

  .review-header {
    ${FLEX.FlexBetweenCenter};
    gap: 16px;
    padding: 2px 2px 14px;
    color: ${({ theme }) => theme.COLORS.WHITE};
  }

  .review-header span {
    font-size: ${calcRem(18)};
    font-weight: 500;
  }

  .review-header strong {
    color: ${({ theme }) => theme.COLORS.BUTTONS3};
    font-size: ${calcRem(14)};
    font-weight: 500;
  }

  .line-item {
    ${FLEX.FlexBetweenStart};
    gap: 18px;
    padding: 18px 2px;
  }

  .line-item + .line-item {
    border-top: 1px solid rgba(115, 129, 164, 0.35);
    padding-top: 18px;
  }

  .item-main {
    ${FLEX.FlexStartStart};
    gap: 14px;
    min-width: 0;
    flex: 1;
  }

  .item-icon {
    ${FLEX.FlexCenterCenter};
    width: 42px;
    height: 42px;
    border-radius: 12px;
    background: ${({ theme }) => `${theme.COLORS.BUTTONS2}18`};
    color: ${({ theme }) => theme.COLORS.BUTTONS2};
    flex-shrink: 0;

    svg {
      width: 20px;
      height: 20px;
    }
  }

  .item-copy {
    min-width: 0;
  }

  .item-copy h3 {
    color: ${({ theme }) => theme.COLORS.WHITE};
    font-size: ${calcRem(16)};
    font-weight: 500;
    margin-bottom: 6px;
  }

  .item-copy p {
    font-size: ${calcRem(14)};
    line-height: 1.45;
  }

  .item-copy .meta {
    color: ${({ theme }) => theme.COLORS.BUTTONS3};
  }

  .item-price {
    ${FLEX.FlexStartEnd};
    flex-direction: column;
    gap: 4px;
    min-width: 110px;
    text-align: right;
  }

  .item-price strong {
    color: ${({ theme }) => theme.COLORS.WHITE};
    font-size: ${calcRem(18)};
    font-weight: 500;
    line-height: 1.1;
  }

  .item-price span {
    color: ${({ theme }) => theme.COLORS.BUTTONS3};
    font-size: ${calcRem(14)};
  }

  .divider {
    margin: 0 -18px 0;
    border-top: 1px solid rgba(115, 129, 164, 0.35);

    &.compact {
      margin-top: 0;
    }
  }

  .summary-row {
    ${FLEX.FlexBetweenCenter};
    gap: 16px;
    padding: 16px 2px;
    color: ${({ theme }) => theme.COLORS.WHITE};
    font-size: ${calcRem(14)};
    font-weight: 500;
  }

  .due-today {
    ${FLEX.FlexBetweenCenter};
    gap: 16px;
    color: ${({ theme }) => theme.COLORS.WHITE};
    padding: 16px 2px 4px;
  }

  .due-today-right {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 4px;
  }

  .due-today strong {
    font-size: ${calcRem(28)};
    font-weight: 500;
    line-height: 1;
  }

  .due-today span {
    font-size: ${calcRem(16)};
    font-weight: 500;
  }

  .due-today-note {
    font-size: ${calcRem(12)};
    color: ${({ theme }) => theme.COLORS.BUTTONS3};
    text-align: right;
  }

  ${mediaQuery.isMobile} {
    padding: 16px;

    .review-header {
      align-items: flex-start;
      flex-direction: column;
      padding-bottom: 12px;
    }

    .line-item {
      align-items: flex-start;
      flex-direction: column;
    }

    .line-item + .line-item {
      padding-top: 16px;
    }

    .item-price {
      min-width: auto;
      text-align: left;
    }

    .divider {
      margin: 0 -16px;
    }
  }
`;

export const ReviewNotice = styled.div`
  ${FLEX.FlexStartCenter};
  gap: 10px;
  margin: 0 auto 24px;
  color: ${({ theme }) => theme.COLORS.BUTTONS3};
  font-size: ${calcRem(14)};
  line-height: 1.45;
  width: 100%;

  svg {
    flex-shrink: 0;
  }
`;

export const FieldGroup = styled.div`
  max-width: 560px;
  margin: 0 auto 28px;

  label {
    display: block;
    margin-bottom: 8px;
    color: ${({ theme }) => theme.COLORS.WHITE};
    font-size: ${calcRem(14)};
    font-weight: 500;
  }

  label span {
    color: ${COLORS.VALIDATERED};
    margin-left: 2px;
  }

  .ant-input {
    height: 40px;
    border-radius: 4px;
    background: #b8bcc8;
    border-color: #b8bcc8;
    color: #2a3140;
  }

  p {
    margin-top: 8px;
    color: ${({ theme }) => theme.COLORS.BUTTONS3};
    font-size: ${calcRem(14)};
    line-height: 1.35;
  }
`;
